"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

import { cn } from "@workspace/ui";
import { Button } from "@workspace/ui/components/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@workspace/ui/components/card";
import { Icons } from "@workspace/ui/components/icons";

import { CtaButton } from "~/components/common/cta/button";
import { DiscordCta } from "~/components/common/cta/discord";
import { useAnalytics } from "~/lib/analytics";
import { DISCORD_REVIEWS, REVIEWS } from "~/lib/constants/reviews";
import { useBreakpoint } from "~/lib/hooks/use-media-query";

const variantToReviews = {
  home: REVIEWS,
  discord: DISCORD_REVIEWS,
} as const;

const ReviewsCta = ({ variant }: { variant: "home" | "discord" }) => (
  <Card className="relative flex min-h-52 flex-col items-center justify-center gap-5 border-2 border-dashed bg-transparent shadow-[inset_0_0_6px_-3px_rgba(0,0,0,0.4)] md:min-h-auto md:gap-6 lg:gap-7 dark:shadow-[inset_0_0_12px_-5px_rgba(255,255,255,0.4)]">
    <span className="text-3xl font-medium sm:text-4xl">Maybe you?</span>
    {variant === "home" ? (
      <CtaButton>Start building now</CtaButton>
    ) : (
      <DiscordCta source="reviews" />
    )}
  </Card>
);

const Review = ({ review }: { review: (typeof REVIEWS)[number] }) => (
  <Card className="flex flex-col justify-between">
    <CardHeader className="flex-row gap-0.5 space-y-0 pb-4">
      {Array.from({
        length: review.rating,
      }).map((_, index) => (
        <Icons.Star
          key={index}
          className="fill-primary w-5"
          aria-label="Star"
        />
      ))}
    </CardHeader>

    <CardContent className="mb-auto pb-6">{review.quote}</CardContent>

    <CardFooter>
      <ReviewAuthor author={review.author} />
    </CardFooter>
  </Card>
);

export const ReviewAuthor = ({
  author,
  className,
}: {
  author: (typeof REVIEWS)[number]["author"];
  className?: string;
}) => (
  <div className={cn("flex flex-row items-center gap-3", className)}>
    <Image
      src={author.avatar}
      alt={author.name}
      width={40}
      height={40}
      sizes="40px"
      className="rounded-full"
    />
    <div className="flex flex-col items-start">
      <div className="flex gap-1">
        <span className="text-sm font-medium">{author.name}</span>
        <div className="flex flex-row items-center gap-1">
          {author.socials?.map((social) => (
            <Link
              href={social.href}
              key={social.href}
              target="_blank"
              rel="noopener noreferrer"
              className="transition-opacity hover:opacity-50"
            >
              <span className="sr-only">{social.href}</span>
              <social.icon
                className={cn("w-4", {
                  "w-3.5": ["instagram.com", "linkedin.com"].some((domain) =>
                    social.href.includes(domain),
                  ),
                })}
              />
            </Link>
          ))}
        </div>
      </div>
      <p className="text-muted-foreground text-sm">{author.position}</p>
    </div>
  </div>
);

export const ReviewsGrid = ({ variant }: { variant: "home" | "discord" }) => {
  const isDesktop = useBreakpoint("lg");
  const { track } = useAnalytics();
  const [allShown, setAllShown] = useState(false);

  const reviews = variantToReviews[variant].filter(
    (review) => !("revenue" in review),
  );
  const initialReviewsToDisplay = isDesktop ? 12 : 6;

  const reviewsToDisplay = allShown
    ? reviews
    : reviews.slice(0, initialReviewsToDisplay - 1);

  return (
    <div className="col-start-2 flex w-full flex-col items-center justify-center gap-8">
      <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
        {reviewsToDisplay.slice(0, 3).map((review) => (
          <Review key={review.author.name} review={review} />
        ))}
        <ReviewsCta variant={variant} />
        {reviewsToDisplay.slice(3).map((review) => (
          <Review key={review.author.name} review={review} />
        ))}
      </div>

      {reviews.length > initialReviewsToDisplay && (
        <Button
          variant="outline"
          className="bg-card dark:hover:bg-card/75 gap-2 rounded-xl px-4 shadow-xs"
          onClick={() => {
            setAllShown((a) => !a);
            track("show_more_click", { type: "reviews" });
          }}
        >
          {allShown ? (
            <Icons.Minus className="text-primary w-3" />
          ) : (
            <Icons.Plus className="text-primary size-4" />
          )}
          See {allShown ? "less" : "all"} reviews
        </Button>
      )}
    </div>
  );
};
